/**
 * API Client (Sprint 13).
 *
 * - Axios + JWT interceptor
 * - 401 → otomatik refresh + retry
 * - Tenant-aware (JWT'de tenant_id var)
 */
import axios, { type AxiosError, type AxiosInstance } from "axios";

const baseURL = import.meta.env["VITE_API_URL"] ?? "http://localhost:8000";

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

// JWT inject
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // FormData (görsel yükleme) gönderiliyorsa instance'ın sabit
  // "application/json" default'unu kaldır — axios o zaman doğru
  // "multipart/form-data; boundary=..." header'ını kendisi üretir.
  // Aksi halde dosya JSON body sayılır → DRF "veri dosya değil" hatası.
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  return config;
});

// 401 → refresh + retry (tek seferlik)
let isRefreshing = false;
let refreshQueue: Array<() => void> = [];

/**
 * Oturumu sonlandır ve login ekranına dön.
 *
 * Bu admin/POS uygulaması HASH router kullanır ve "login" ayrı bir route değil;
 * `isAuthenticated` (≈ localStorage.auth_token) false olunca App.tsx otomatik
 * <LoginPage /> render eder. Bu yüzden eski `window.location.href = "/login"`
 * mantığı yanlıştı: gerçek bir /login path'i yok ve dev sunucusu pathname'i
 * "/login" yapınca guard redirect'i tamamen atlıyordu → token temizlense bile
 * React re-render olmuyor, kullanıcı boş yetkisiz ekranda kalıyordu.
 *
 * Çözüm: token'ları temizle + tam sayfa reload. Reload sonrası loadState()
 * token bulamaz → LoginPage çizilir. Zaten anonimsek (token yok) reload yapma —
 * login ekranındaki bir 401 sonsuz reload döngüsü yaratmasın.
 */
function forceLogout(): void {
  const wasAuthed = !!localStorage.getItem("auth_token");
  localStorage.removeItem("auth_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("auth_user");
  localStorage.removeItem("auth_tenant");
  localStorage.removeItem("auth_branch");
  localStorage.removeItem("auth_role");
  localStorage.removeItem("auth_memberships");
  localStorage.removeItem("auth_permissions");
  localStorage.removeItem("auth_menu_hidden");
  if (wasAuthed) {
    window.location.reload();
  }
}

apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as any;
    // /qr/* endpoint'leri public müşteri (QR menü) akışıdır — staff JWT refresh
    // veya staff login'e reload BURADA tetiklenmemeli. QR'da müşteri sadece
    // customer_token tutar (staff auth_token yok); bir QR 401'i müşteriyi staff
    // login ekranına atıp tam reload yapmamalı. /auth/ ise login/refresh/me
    // çağrılarının kendisi (kendi 401'ini handle eder).
    const isExempt =
      original.url?.includes("/auth/") || original.url?.includes("/qr/");
    if (error.response?.status === 401 && !original._retry && !isExempt) {
      original._retry = true;
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push(() => resolve(apiClient(original)));
        });
      }
      isRefreshing = true;
      try {
        const refresh = localStorage.getItem("refresh_token");
        if (!refresh) throw new Error("no refresh");
        const res = await axios.post(`${baseURL}/api/auth/refresh/`, {
          refresh,
        });
        const newAccess = res.data.access;
        localStorage.setItem("auth_token", newAccess);
        refreshQueue.forEach((cb) => cb());
        refreshQueue = [];
        return apiClient(original);
      } catch {
        forceLogout();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  },
);

// Idempotency helper
export function withIdempotency<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const interceptor = apiClient.interceptors.request.use((config) => {
    if (config.method === "post") {
      config.headers["Idempotency-Key"] = key;
    }
    return config;
  });
  return fn().finally(() => apiClient.interceptors.request.eject(interceptor));
}
